import {
  EyeOutlined,
  FileOutlined,
  LoadingOutlined,
  ReloadOutlined
} from "@ant-design/icons"
import { Skeleton, type UploadFile } from "antd"
import { Button, Card, Flex, Image, Progress, Space, Typography } from "antd"
import React, { memo, useState } from "react"

import type { Attachment } from "../../../../../../Album/types/attachment.types.ts"
import {
  formatFileSize,
  getFileIcon,
  isImage
} from "../../../../helpers/file.tsx"

const { Text } = Typography

interface RecentFileCardProps {
  file: UploadFile
  onClick?: (file: Attachment) => void
  // onCancelUpload?: (uid: string) => void
  onRetry?: (uid: string) => void
  onPreviewFile: (file: UploadFile) => void
}

export const RecentFileCard = memo(
  ({
    file,
    onClick,
    // onCancelUpload,
    onPreviewFile,
    onRetry
  }: RecentFileCardProps) => {
    const [previewImage, setPreviewImage] = useState(false)

    const isUploading = file.status === "uploading"
    const isError = file.status === "error"
    const isDone = file.status === "done"

    const attachment = file.response as Attachment | undefined
    const mimeType = attachment?.mime_type || file.type || ""
    const isImageType = isImage(mimeType)
    const fileUrl = attachment?.file || file.url

    const handleClick = () => {
      if (isDone && file.response) {
        onClick?.(file.response)
      }
    }

    const handlePreview = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (isImageType) {
        setPreviewImage(true)
      } else {
        onPreviewFile(file)
      }
    }

    const renderPreview = () => {
      // Uploading state
      if (isUploading) {
        return (
          <div className="size-full flex flex-col items-center justify-center bg-gray-50 gap-2">
            <LoadingOutlined className="text-2xl text-blue-500" />
            <Progress
              percent={file.percent || 0}
              size="small"
              className="w-3/4"
              showInfo={false}
            />
            {/*<Tooltip title="Cancel upload">*/}
            {/*  <Button*/}
            {/*    type="text"*/}
            {/*    size="small"*/}
            {/*    icon={<CloseOutlined />}*/}
            {/*    onClick={(e) => {*/}
            {/*      e.stopPropagation()*/}
            {/*      onCancelUpload?.(file.uid)*/}
            {/*    }}*/}
            {/*    className="text-gray-400 hover:text-red-500"*/}
            {/*  />*/}
            {/*</Tooltip>*/}
          </div>
        )
      }

      // Error state
      if (isError) {
        return (
          <div className="size-full flex flex-col items-center justify-center bg-red-50 gap-2">
            <FileOutlined className="text-3xl text-red-400" />
            <Text type="danger" className="text-xs">
              Upload failed
            </Text>
            {onRetry && (
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                onClick={(e) => {
                  e.stopPropagation()
                  onRetry(file.uid)
                }}
              >
                Retry
              </Button>
            )}
          </div>
        )
      }

      // Image preview with hover overlay
      if (isImageType && fileUrl) {
        return (
          <div className="relative group size-full">
            <Flex
              onClick={handlePreview}
              justify="center"
              align="center"
              className="absolute z-10 inset-0 bg-black/50 group-hover:opacity-100 transition-all duration-500 opacity-0 cursor-pointer"
            >
              <Space className="text-white text-shadow-lg font-bold">
                <EyeOutlined className="text-lg" />
                Preview
              </Space>
            </Flex>
            <Image
              loading={"lazy"}
              preview={{
                mask: null,
                visible: previewImage,
                src: fileUrl,
                onVisibleChange: (value) => !value && setPreviewImage(value)
              }}
              src={fileUrl}
              alt={file.name}
              rootClassName="size-full"
              className="size-full object-contain"
              placeholder={<Skeleton.Node active className={"size-full"} />}
            />
          </div>
        )
      }

      // Other file types with hover overlay
      return (
        <div className="relative group size-full">
          <Flex
            onClick={handlePreview}
            justify="center"
            align="center"
            className="absolute z-10 inset-0 bg-black/50 group-hover:opacity-100 transition-all duration-500 opacity-0 cursor-pointer"
          >
            <Space className="text-white text-shadow-lg font-bold">
              <EyeOutlined className="text-lg" />
              Preview
            </Space>
          </Flex>
          <div className="size-full flex items-center justify-center bg-primary/10">
            <div className="[&_svg]:text-5xl">{getFileIcon(file.type)}</div>
          </div>
        </div>
      )
    }

    return (
      <Card
        hoverable={isDone}
        className={`overflow-hidden ${isError ? "border-red-300" : ""}`}
        styles={{ body: { padding: 8 } }}
        onClick={handleClick}
      >
        <div className="h-20 rounded overflow-hidden mb-2">
          {renderPreview()}
        </div>
        <Text
          className="text-xs block"
          ellipsis={{ tooltip: file.name }}
          type={isError ? "danger" : undefined}
        >
          {file.name}
        </Text>
        {isDone && file.size && (
          <Text type="secondary" className="text-xs">
            {formatFileSize(file.size)}
          </Text>
        )}
        {isUploading && (
          <Text type="secondary" className="text-xs">
            Uploading...
          </Text>
        )}
      </Card>
    )
  }
)

RecentFileCard.displayName = "RecentFileCard"
